/**
 * Tenant security alerts page — ``/security/alerts``.
 *
 * Sprint 129 Stream F closure. Lists tenant-visible alerts (those tagged
 * ``tenant_visible: "true"`` in PrometheusRule) with severity badges,
 * status filter, and one-click acknowledge.
 *
 * Theming (ISS-446, Sprint 234): usa tokens de tema shadcn/tailwind
 * (bg-background / bg-card / text-foreground / text-muted-foreground / border)
 * en lugar de clases oscuras hardcodeadas — coherente con el tema claro de la
 * UI de DR (y adaptativo dark).
 */

import { useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  ExternalLink,
  RefreshCw,
  ShieldAlert,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useAcknowledgeAlert,
  useTenantSecurityAlerts,
} from '../hooks/use-security-alerts';
import { useTenant } from '@/contexts/TenantContext';
import { useI18n } from '@/i18n';
import type { AlertSeverity, AlertStatus, TenantSecurityAlert } from '../types';

/**
 * Tenant-first: solo se renderizan labels de negocio del tenant. El API ya
 * aplica esta allow-list; la UI la repite como defensa en profundidad.
 */
const PUBLIC_LABELS = new Set([
  'data_type',
  'action',
  'phase',
  'reason',
  'resource',
  'feature',
  'kind',
]);

/** Título humano por tipo de alerta (nunca el identificador interno). */
const ALERT_TITLE_KEYS: Record<string, string> = {
  TenantBruteForceLogin: 'bruteForce',
  LLMTokenBudgetBurnRate: 'tokenBudget',
  TenantPlanLimitDenialsHigh: 'planLimit',
  TenantStorageQuotaDenialsHigh: 'storageQuota',
  CostCenterBudgetExceeded: 'costCenter',
  'DGV-pii': 'dgvPii',
  'DGV-credential': 'dgvCredential',
  'DGV-code_danger': 'dgvCodeDanger',
  'DGV-violence': 'dgvViolence',
  'DGV-hate': 'dgvHate',
  FrontierRunFailed: 'frontierRunFailed',
  FrontierRunBlocked: 'frontierRunBlocked',
  FrontierRunExpired: 'frontierRunExpired',
  FrontierRunMissed: 'frontierRunMissed',
  FrontierRoutineAutoPaused: 'frontierRoutineAutoPaused',
  FrontierInterventionPending: 'frontierInterventionPending',
  FrontierEmergencyStop: 'frontierEmergencyStop',
};

export function alertTitle(
  alertname: string,
  t: (key: string) => string,
): string {
  const key = ALERT_TITLE_KEYS[alertname];
  return t(`admin.securityAlerts.alerts.${key ?? 'unknown'}`);
}

const SEVERITY_BADGE: Record<AlertSeverity, string> = {
  P0: 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/40 dark:text-red-300 dark:border-red-800',
  P1: 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-800',
  P2: 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-800',
  // P3 — avisos agregados (p. ej. DGV log_only, REQ-SM-021).
  P3: 'bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800/60 dark:text-slate-300 dark:border-slate-700',
};

const STATUS_BADGE: Record<AlertStatus, string> = {
  firing: 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/40 dark:text-red-300 dark:border-red-800',
  acknowledged: 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-800',
  resolved: 'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-300 dark:border-emerald-800',
};

export default function SecurityAlertsPage() {
  const [statusFilter, setStatusFilter] = useState<string>('firing');
  const [selected, setSelected] = useState<TenantSecurityAlert | null>(null);
  const { currentTenant } = useTenant();
  const { t } = useI18n();
  const q = useTenantSecurityAlerts({
    status: statusFilter === 'all' ? undefined : statusFilter,
    limit: 100,
  });
  const ack = useAcknowledgeAlert();

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center gap-3">
        <ShieldAlert className="w-6 h-6 text-amber-500 dark:text-amber-400" />
        <div>
          <h1 className="text-xl font-semibold text-foreground">Security alerts</h1>
          <div className="text-xs text-muted-foreground">
            Active security signals affecting your tenant. Acknowledging an
            alert is logged as a tenant-side audit event.
          </div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-background border border-input rounded text-xs px-2 py-1 text-foreground"
            data-testid="alert-status-filter"
          >
            <option value="firing">Firing</option>
            <option value="acknowledged">Acknowledged</option>
            <option value="resolved">Resolved</option>
            <option value="all">All</option>
          </select>
          <button
            onClick={() => q.refetch()}
            className="p-1.5 rounded bg-secondary hover:bg-secondary/70 text-secondary-foreground"
            title="Refresh"
            aria-label="Refresh alerts"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {q.isLoading && (
        <div className="text-muted-foreground text-sm">Loading alerts…</div>
      )}
      {q.isError && (
        <div className="bg-card border border-destructive/40 rounded-lg p-4 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-destructive mt-0.5" />
          <div className="text-sm text-foreground">
            No se pudieron cargar las alertas de seguridad.
            <div className="text-xs text-muted-foreground mt-1">
              Introduce un ticket si el problema persiste.
            </div>
          </div>
        </div>
      )}

      {q.data && q.data.items.length === 0 && (
        <div className="bg-card border border-border rounded-lg p-12 text-center">
          <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-2" />
          <div className="text-foreground">All clear</div>
          <div className="text-xs text-muted-foreground">
            No alerts match the current filter.
          </div>
        </div>
      )}

      {q.data && q.data.items.length > 0 && (
        <div className="bg-card border border-border rounded-lg overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-muted text-[10px] uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="py-2 px-3">Severity</th>
                <th className="py-2 px-3">Alert</th>
                <th className="py-2 px-3">Status</th>
                <th className="py-2 px-3">Started</th>
                <th className="py-2 px-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody data-testid="alerts-table-body">
              {q.data.items.map((a) => (
                <tr
                  key={a.id}
                  onClick={() => setSelected(a)}
                  className="border-b border-border hover:bg-accent/40 cursor-pointer"
                  data-testid={`alert-row-${a.id}`}
                >
                  <td className="py-2 px-3">
                    <span
                      className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${SEVERITY_BADGE[a.severity]}`}
                    >
                      {a.severity}
                    </span>
                  </td>
                  <td className="py-2 px-3">
                    <div className="text-sm text-foreground flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3 text-amber-500 dark:text-amber-400" />
                      {alertTitle(a.alertname, t)}
                    </div>
                    <div className="text-xs text-muted-foreground">{a.summary}</div>
                  </td>
                  <td className="py-2 px-3">
                    <span
                      className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${STATUS_BADGE[a.status]}`}
                    >
                      {a.status}
                    </span>
                  </td>
                  <td className="py-2 px-3 text-xs text-muted-foreground">
                    {new Date(a.started_at).toLocaleString()}
                  </td>
                  <td className="py-2 px-3 text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="flex justify-end gap-2">
                      {a.runbook_url && (
                        <a
                          href={a.runbook_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-2 py-1 text-xs rounded bg-secondary hover:bg-secondary/70 text-secondary-foreground inline-flex items-center gap-1"
                        >
                          Runbook <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                      {a.status === 'firing' && (
                        <button
                          onClick={() => ack.mutate(a.id)}
                          disabled={ack.isPending}
                          className="px-2 py-1 text-xs rounded bg-primary hover:bg-primary/90 text-primary-foreground disabled:opacity-50"
                          data-testid={`ack-${a.id}`}
                        >
                          Acknowledge
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail modal — los eventos son clicables (25-Sep-2026) */}
      <Dialog open={selected !== null} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent className="max-w-lg">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-500" />
                  {alertTitle(selected.alertname, t)}
                </DialogTitle>
                <DialogDescription className="flex items-center gap-2 pt-1">
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${SEVERITY_BADGE[selected.severity]}`}>
                    {selected.severity}
                  </span>
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${STATUS_BADGE[selected.status]}`}>
                    {selected.status}
                  </span>
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-3 text-sm">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Summary</div>
                  <div className="text-foreground">{selected.summary}</div>
                </div>

                {selected.description && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Description</div>
                    <div className="text-foreground whitespace-pre-wrap">{selected.description}</div>
                  </div>
                )}

                {Object.keys(selected.labels).length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
                      {t('admin.securityAlerts.labels')}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {Object.entries(selected.labels)
                        .filter(([k]) => PUBLIC_LABELS.has(k))
                        .map(([k, v]) => (
                        <span
                          key={k}
                          className="text-[11px] px-2 py-0.5 rounded bg-muted text-foreground border border-border font-mono"
                        >
                          {k}: {String(v)}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Contexto del evento (trazabilidad) — resuelto sin persistir PII */}
                <div className="space-y-1 text-xs text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-wider w-24">Tenant</span>
                    <span className="text-foreground">{currentTenant?.name ?? '—'}</span>
                  </div>
                  {selected.user && (
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] uppercase tracking-wider w-24">
                        {t('admin.securityAlerts.user')}
                      </span>
                      <span className="text-foreground" data-testid="alert-user">
                        {selected.user.email ||
                          selected.user.name ||
                          t('admin.securityAlerts.userUnavailable')}
                      </span>
                    </div>
                  )}
                  {selected.conversation && (
                    <>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] uppercase tracking-wider w-24">
                          {t('admin.securityAlerts.conversation')}
                        </span>
                        <span className="text-foreground" data-testid="alert-conversation">
                          {selected.conversation.title ||
                            t('admin.securityAlerts.conversationUntitled')}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] uppercase tracking-wider w-24">
                          {t('admin.securityAlerts.reference')}
                        </span>
                        <span className="text-foreground font-mono" data-testid="alert-conversation-ref">
                          {selected.conversation.reference}
                        </span>
                      </div>
                    </>
                  )}
                </div>

                <div className="space-y-1 text-xs text-muted-foreground border-t border-border pt-2">
                  <div className="flex items-center gap-2">
                    <Clock className="w-3.5 h-3.5" />
                    Started: {new Date(selected.started_at).toLocaleString()}
                  </div>
                  {selected.resolved_at && (
                    <div className="flex items-center gap-2">
                      <Clock className="w-3.5 h-3.5" />
                      Resolved: {new Date(selected.resolved_at).toLocaleString()}
                    </div>
                  )}
                  {selected.acknowledged_at && (
                    <div className="flex items-center gap-2">
                      <Clock className="w-3.5 h-3.5" />
                      Acknowledged: {new Date(selected.acknowledged_at).toLocaleString()}
                    </div>
                  )}
                </div>
              </div>

              <DialogFooter className="flex items-center justify-end gap-2">
                {selected.status === 'firing' && (
                  <button
                    onClick={() => {
                      ack.mutate(selected.id, {
                        onSuccess: () => setSelected(null),
                      });
                    }}
                    disabled={ack.isPending}
                    className="px-3 py-1.5 text-xs rounded bg-primary hover:bg-primary/90 text-primary-foreground disabled:opacity-50"
                    data-testid="modal-ack"
                  >
                    Acknowledge
                  </button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}